import gql from 'graphql-tag';
import { useQuery } from '@apollo/client';
import { useSelector } from '~Hooks';
import { STAFF_PERMISSIONS } from '~../__queries__/globalTypes';
import { getStaff, getCompanyStaff } from './__queries__';
import { useTableUniverse } from '~Components/TableUniverse';
import { useCallback } from 'react';

// All permissions
const Permissions: STAFF_PERMISSIONS[] = [
  STAFF_PERMISSIONS.ANALYTICS,
  STAFF_PERMISSIONS.CLOSE_DAY,
  STAFF_PERMISSIONS.CREATE_INVOICE_TARGET,
  STAFF_PERMISSIONS.HISTORY,
  STAFF_PERMISSIONS.INVOICE_PURCHASE,
  STAFF_PERMISSIONS.LOG_OUT,
  STAFF_PERMISSIONS.MANUAL_DISCOUNT,
  STAFF_PERMISSIONS.PRESET_DISCOUNT,
  STAFF_PERMISSIONS.REFUND,
  STAFF_PERMISSIONS.REPORTS,
  STAFF_PERMISSIONS.SETTINGS,
  STAFF_PERMISSIONS.ADD_ITEM_TO_TABLE,
  STAFF_PERMISSIONS.ASSUME_ROLE,
  STAFF_PERMISSIONS.VOID_PAYMENT,
  STAFF_PERMISSIONS.MAKE_PAYMENT,
  STAFF_PERMISSIONS.CREATE_CHEQUE,
  STAFF_PERMISSIONS.GIFT_CARDS,
];

// Permissions for main menu
const MainMenuPermissions = [
  STAFF_PERMISSIONS.HISTORY,
  STAFF_PERMISSIONS.LOG_OUT,
  STAFF_PERMISSIONS.REPORTS,
  STAFF_PERMISSIONS.SETTINGS,
  STAFF_PERMISSIONS.REFUND,
  STAFF_PERMISSIONS.CLOSE_DAY,
  STAFF_PERMISSIONS.ASSUME_ROLE,
];

const STAFF = gql`
  query getStaff($id: ID!) {
    staff: Staff(id: $id) {
      id
      name
      code
      can {
        LOG_OUT
        REPORTS
        ANALYTICS
        PRESET_DISCOUNT
        MANUAL_DISCOUNT
        REFUND
        HISTORY
        CLOSE_DAY
        SETTINGS
        INVOICE_PURCHASE
        CREATE_CHEQUE
        MAKE_PAYMENT
        VOID_PAYMENT
        ASSUME_ROLE
        ADD_ITEM_TO_TABLE
        CREATE_INVOICE_TARGET
        GIFT_CARDS
      }
    }
  }
`;

const COMPANY_STAFF = gql`
  query getCompanyStaff($companyId: ID!) {
    company: Company(id: $companyId) {
      id
      staffs {
        id
      }
    }
  }
`;

type CompanyPermission = {
  [permission in STAFF_PERMISSIONS]: boolean;
};

type StaffCan = CompanyPermission & {
  isLoggedIn: boolean;
  openMainMenu: boolean;
  multi: (permissions: STAFF_PERMISSIONS[], mustHaveAll?: boolean) => boolean;
  mutateCheque: (chequeId: string) => boolean;
  mutateTable: (tableId: string) => boolean;
  canUsePaymentMethod: (paymentMethodId: string) => boolean;
};

export const useStaffCan = (): StaffCan => {
  const staffs = useSelector((state) => state.companyStaff?.staffs ?? []);
  const staffId = useSelector((state) => state.staff?.id ?? '');
  const companyId = useSelector((state) => state.company);
  const venueId = useSelector((state) => state.venueId) as string;
  const paymentMethods = useSelector((state) => state.POSPaymentMethods);
  const universe = useTableUniverse();

  const { data: staffData } = useQuery<getStaff>(STAFF, {
    variables: { id: staffId },
    skip: !staffId,
  });

  const { data: companyStaffData } = useQuery<getCompanyStaff>(COMPANY_STAFF, {
    variables: { companyId },
    skip: !companyId,
  });

  const companyStaff = companyStaffData?.company?.staffs ?? [];

  const hasPermission = (permission: STAFF_PERMISSIONS) => {
    // Allow if company hasn't set up any staff
    if (!companyStaff.length) {
      return true;
    }

    return !!staffData?.staff.can[permission];
  };

  const hasMultiPermissions = (permissions: STAFF_PERMISSIONS[], mustHaveAll = false) => {
    if (!permissions.length) {
      return true;
    }

    if (mustHaveAll) {
      return permissions.every(hasPermission);
    } else {
      return permissions.some(hasPermission);
    }
  };

  const companyPermissions = Permissions.reduce(
    (result, permission) => ({
      ...result,
      [permission]: hasPermission(permission),
    }),
    {} as CompanyPermission,
  );

  const canMutateCheque = (chequeId: string) => {
    // If staffCodes arent used you can always mutate checks
    if (!companyStaff.length) {
      return true;
    }
    // If staff is owner of cheque or has permission REFUND (temporary solution)
    const cheque = universe.cheques.find((c) => c.id === chequeId);
    return (
      cheque?.owner === staffId ||
      hasPermission(STAFF_PERMISSIONS.REFUND) ||
      hasPermission(STAFF_PERMISSIONS.ASSUME_ROLE)
    );
  };

  const canMutateTable = (tableId: string) => {
    // If staffCodes arent used you can always mutate checks
    if (!companyStaff.length) {
      return true;
    }
    // If staff is owner of any cheque on table or there are no cheques or has permission REFUND (temporary solution)
    const tableCheques = universe.cheques.filter((c) => c.tableId === tableId);
    const staffTableCheques = tableCheques.filter((c) => c.owner === staffId);
    return (
      tableCheques.length === 0 ||
      staffTableCheques.length > 0 ||
      hasPermission(STAFF_PERMISSIONS.ASSUME_ROLE) ||
      hasPermission(STAFF_PERMISSIONS.REFUND)
    );
  };

  const canUsePaymentMethod = useCallback(
    (paymentMethodId: string) => {
      const paymentMethod = paymentMethods.find((pm) => pm.id === paymentMethodId);

      if (!paymentMethod) {
        return false;
      }

      if (paymentMethod?.venues?.length > 0 && !paymentMethod?.venues.includes(venueId)) {
        return false;
      }

      // If staffCodes aren't used you can use the payment method
      if (!companyStaff.length) {
        return true;
      }

      const thisStaff = staffs.find((s) => s.id === staffId);

      if (
        paymentMethod?.accessLevels?.length > 0 &&
        thisStaff?.accessLevel &&
        !paymentMethod?.accessLevels.includes(thisStaff?.accessLevel.id)
      ) {
        return false;
      }

      return true;
    },
    [paymentMethods, venueId, companyStaff, staffs, staffId],
  );

  return {
    /**
     * Permissions from company
     */
    ...companyPermissions,

    /**
     * Check if staff is logged in
     */
    isLoggedIn: !!staffId,

    /**
     * Check multiple permissions at once
     * Passing second argument as true will check all permissions
     */
    multi: (permissions, mustHaveAll) => hasMultiPermissions(permissions, mustHaveAll),

    /**
     * Check if user has any permission required by main menu
     */
    openMainMenu: hasMultiPermissions(MainMenuPermissions),

    /**
     * Check if staff is owner of cheque or has permissions to mutate cheque
     */
    mutateCheque: (chequeId: string) => canMutateCheque(chequeId),

    /**
     * Check if staff is owner of any cheques on table (or its empty) or has permissions to mutate cheque
     */
    mutateTable: (tableId: string) => canMutateTable(tableId),

    /**
     *  Check if staff can use a given payment method
     */
    canUsePaymentMethod,
  };
};
